import { ConnectionTypeEnum } from "@api/rest/types-and-hooks";

/**
 * Auth0 connection types enabled on every organization created from Basecamp.
 *
 * Both are always enabled so the Google connection doesn't have to be turned on
 * by hand in Auth0 afterwards. username_password stays enabled because the
 * per-user invite type still defaults to it, and users can only be invited with
 * a connection type that is enabled on the org.
 */
export const ORG_CONNECTION_TYPES: ConnectionTypeEnum[] = [
  ConnectionTypeEnum.google_oauth,
  ConnectionTypeEnum.username_password,
];

/**
 * How the EHR sync gate behaves for an organization type (see `getEhrSyncMode`).
 *
 * - EDITABLE: any non-graduated type; `ehr_sync_enabled` is stored as sent.
 * - ALWAYS_ON: a graduated integration; the backend ignores the flag.
 * - UNSELECTED: no type chosen yet (create form only).
 */
export const EhrSyncMode = {
  EDITABLE: "editable",
  ALWAYS_ON: "always_on",
  UNSELECTED: "unselected",
} as const;
export type EhrSyncMode = (typeof EhrSyncMode)[keyof typeof EhrSyncMode];

export const ReviewSettingKey = {
  PEER_REVIEW_ENABLED: "peer_review_enabled",
  REQUIRE_APPROVAL_BEFORE_EXPORT: "require_approval_before_export",
  FINAL_APPROVER_WAITS_FOR_REVIEWERS: "final_approver_waits_for_reviewers",
} as const;
export type ReviewSettingKey = (typeof ReviewSettingKey)[keyof typeof ReviewSettingKey];

/** Used only if the API omits a field. Peer review is on by default for every org. */
export const REVIEW_SETTING_DEFAULTS: Record<ReviewSettingKey, boolean> = {
  [ReviewSettingKey.PEER_REVIEW_ENABLED]: true,
  [ReviewSettingKey.REQUIRE_APPROVAL_BEFORE_EXPORT]: false,
  [ReviewSettingKey.FINAL_APPROVER_WAITS_FOR_REVIEWERS]: false,
};

/** Reads a DRF error body: `detail`, else the first message of the first field. */
export const reviewSettingsErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== "object") return null;
  const body = data as Record<string, unknown>;
  if (typeof body.detail === "string") return body.detail;
  for (const value of Object.values(body)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === "string") return first;
  }
  return null;
};

export const REVIEW_SETTINGS: { key: ReviewSettingKey; label: string; tooltip: string }[] = [
  {
    key: ReviewSettingKey.PEER_REVIEW_ENABLED,
    label: "Team review enabled",
    tooltip:
      "Turns Team review on for this organization. When off, the review UI is hidden and review requests and decisions are blocked, and the two settings below have no effect.",
  },
  {
    key: ReviewSettingKey.REQUIRE_APPROVAL_BEFORE_EXPORT,
    label: "Require approval before export",
    tooltip:
      "When Team review is on, reports cannot be completed, signed or exported until every active reviewer has approved. Applies immediately to in-progress reports, including ones with no reviewers. Templates can override this.",
  },
  {
    key: ReviewSettingKey.FINAL_APPROVER_WAITS_FOR_REVIEWERS,
    label: "Final approver waits for reviewers",
    tooltip:
      "When Team review is on, the final approver can approve, and is notified, only after every other active reviewer has approved. Templates can override this.",
  },
];

/** A Frontera EHR org's vertical organization id (`external_id`) is a uuid; surrounding spaces are trimmed on save. */
export const VERTICAL_ORG_ID_PATTERN = /^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/i;

export const VERTICAL_ORG_ID_COPY = {
  label: "Vertical organization ID",
  help: "The organization's id in Frontera EHR (vertical). Programming syncs use it to find the right clinic.",
  invalid: "Vertical organization ID must be a UUID",
  saved: "Vertical organization ID updated",
  saveFailed: "Failed to update the vertical organization ID",
  noPermission: "Only Basecamp admins and enablement admins can change the vertical organization ID.",
};
